import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import { loadArticleHtml, prepareArticleQuill } from '../../utils/articlePaste';
import styles from './ESPCourses.module.css';
import { 
  faculties, 
  specialtiesData, 
  academicLevels,
  getFacultyById,
  getSpecialtiesByFacultyId,
  isValidFacultyId,
  isValidSpecialtyForFaculty
} from '../../data/espData';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ESPCourses = ({ onBack, selectedLanguage = 'English' }) => {
  const { t } = useTranslation('espCourses');
  const [activeFaculty, setActiveFaculty] = useState(faculties[0].id);
  const [activeSpecialty, setActiveSpecialty] = useState(null);
  const [activeAcademicLevel, setActiveAcademicLevel] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    content: '',
    language: selectedLanguage || 'English',
    specialty: '',
    faculty: '',
    academic_year: ''
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [useQuill, setUseQuill] = useState(false);
  const quillRef = useRef(null);
  const [editingArticleId, setEditingArticleId] = useState(null);

  useEffect(() => {
    if (showAddModal && window.Quill && quillRef.current && !useQuill) {
      setUseQuill(true);
      quillRef.current.innerHTML = '';
      const quill = new window.Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link', 'blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'header': [1, 2, 3, false] }],
            ['clean']
          ]
        },
        placeholder: t('form.contentPlaceholder')
      });
      quill.on('text-change', () => {
        setAddForm(f => ({ ...f, content: quill.root.innerHTML }));
      });
      prepareArticleQuill(quill);
    }
    // eslint-disable-next-line
  }, [showAddModal]);

  const handleFacultyClick = (facultyId) => {
    setActiveFaculty(facultyId);
    setActiveSpecialty(null);
    setActiveAcademicLevel(null);
    setArticles([]);
  };

  const handleSpecialtyClick = (specialty) => {
    setActiveSpecialty(specialty);
    setActiveAcademicLevel(null);
    setArticles([]);
  };

  const handleAcademicLevelClick = (level) => {
    setActiveAcademicLevel(level);
    setArticles([]);
    
    // Fetch articles for the selected specialty and academic level
    if (activeSpecialty) {
      setLoading(true);
      setError('');
      
      // Get the faculty label from the active faculty ID
      const facultyLabel = getFacultyById(activeFaculty)?.label;
      
      const langParam = selectedLanguage ? `&language=${encodeURIComponent(selectedLanguage)}` : '';
      fetch(`${API_BASE_URL}/api/esp-articles?specialty=${encodeURIComponent(activeSpecialty)}&academicLevel=${encodeURIComponent(level.label)}&faculty=${encodeURIComponent(facultyLabel)}${langParam}`)
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch articles');
          return res.json();
        })
        .then(data => {
          setArticles(data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  };

  const handleCloseSpecialty = () => {
    setActiveSpecialty(null);
    setActiveAcademicLevel(null);
    setArticles([]);
    setError('');
  };

  const handleCloseAcademicLevel = () => {
    setActiveAcademicLevel(null);
    setArticles([]);
    setError('');
  };

  useEffect(() => {
    if (activeSpecialty && activeAcademicLevel) {
      setLoading(true);
      setError('');
      // Fetch articles for specific specialty and academic level
      const langParam = selectedLanguage ? `&language=${encodeURIComponent(selectedLanguage)}` : '';
      fetch(`${API_BASE_URL}/api/esp-articles?specialty=${encodeURIComponent(activeSpecialty)}&academicLevel=${encodeURIComponent(activeAcademicLevel.label)}${langParam}`)
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch articles');
          return res.json();
        })
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setArticles(data);
          } else {
            setArticles([]); // No mockup data - show empty state
          }
          setLoading(false);
        })
        .catch(err => {
          setError(t('messages.errorFetching'));
          setLoading(false);
        });
    }
  }, [activeSpecialty, activeAcademicLevel, selectedLanguage]);

  const handleEditArticle = (article) => {
    setEditingArticleId(article.id);
    setAddForm({
      title: article.title || '',
      content: article.content || '',
      language: article.language || 'English',
      specialty: article.specialty || activeSpecialty || '',
      faculty: article.faculty || '',
      academic_year: article.academic_year || ''
    });
    setShowAddModal(true);
    setAddError('');
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) loadArticleHtml(quill, article.content || '');
      }
    }, 200);
  };

  const handleDeleteArticle = (id) => {
    if (!window.confirm(t('messages.deleteConfirm'))) return;
    fetch(`${API_BASE_URL}/api/esp-articles/${id}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete article');
        // Refresh articles
        setLoading(true);
        const langParam = selectedLanguage ? `&language=${encodeURIComponent(selectedLanguage)}` : '';
      fetch(`${API_BASE_URL}/api/esp-articles?specialty=${encodeURIComponent(activeSpecialty)}&academicLevel=${encodeURIComponent(activeAcademicLevel.label)}${langParam}`)
          .then(res => res.json())
          .then(data => {
            setArticles(data);
            setLoading(false);
          });
      })
      .catch(() => {
        logger.error('Failed to delete article');
        alert(t('messages.errorDeleting'));
      });
  };

  const handleAddArticle = () => {
    setShowAddModal(true);
    const currentFacultyObj = faculties.find(f => f.id === activeFaculty);
    const currentSpecialty = activeSpecialty || '';
    const currentAcademicYear = activeAcademicLevel ? activeAcademicLevel.label : '';
    
    setAddForm({ 
      title: '', 
      content: '', 
      language: selectedLanguage || 'English', 
      specialty: currentSpecialty,
      faculty: currentFacultyObj ? currentFacultyObj.label : '',
      academic_year: currentAcademicYear
    });
    setAddError('');
    setEditingArticleId(null);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm(f => ({ ...f, [name]: value }));
  };

  const handleAddFormSubmit = (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError('');
    
    // Get the faculty label from the selected faculty ID
    const facultyLabel = getFacultyById(addForm.faculty)?.label || addForm.faculty;
    
    const method = editingArticleId ? 'PUT' : 'POST';
    const url = editingArticleId
      ? `${API_BASE_URL}/api/esp-articles/${editingArticleId}`
      : `${API_BASE_URL}/api/esp-articles`;
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: addForm.title,
        content: addForm.content,
        language: addForm.language,
        specialty: addForm.specialty,
        faculty: facultyLabel,
        academic_year: addForm.academic_year,
        teacher_id: 1, // Default teacher ID, you can make this dynamic
        status: 'published'
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to save article');
        return res.json();
      })
      .then(() => {
        setShowAddModal(false);
        setAddLoading(false);
        setEditingArticleId(null);
        // Refresh articles
        setLoading(true);
        const facultyLabel = getFacultyById(activeFaculty)?.label;
        const langParam = selectedLanguage ? `&language=${encodeURIComponent(selectedLanguage)}` : '';
        fetch(`${API_BASE_URL}/api/esp-articles?specialty=${encodeURIComponent(activeSpecialty)}&academicLevel=${encodeURIComponent(activeAcademicLevel.label)}&faculty=${encodeURIComponent(facultyLabel)}${langParam}`)
          .then(res => res.json())
          .then(data => {
            setArticles(data);
            setLoading(false);
          });
      })
      .catch((err) => {
        logger.error('Failed to save article:', err);
        setAddError(t('messages.errorSaving'));
        setAddLoading(false);
      });
  };

  return (
    <div className={styles.espCoursesScrollWrapper}>
      <div className={styles.espCoursesWrapper}>
        <div className={styles.header}>
          <div className={styles.buttonContainer}>
            <button className={styles.mainBackBtn} onClick={onBack}>
              <span className="material-icons">arrow_back</span>
              {t('backToCourseManagement')}
            </button>
          </div>
          <h1>{t('title')}</h1>
          <p>{t('subtitle')}</p>
        </div>



        {/* Horizontal Tabs for Navigation */}
        <div className={styles.horizontalTabs}>
          {faculties.map(faculty => (
            <button
              key={faculty.id}
              className={
                activeFaculty === faculty.id && !activeSpecialty
                  ? `${styles.tabButton} ${styles.active}`
                  : styles.tabButton
              }
              onClick={() => {
                setActiveFaculty(faculty.id);
                setActiveSpecialty(null);
                setActiveAcademicLevel(null);
              }}
            >
              <span className="material-icons">{faculty.icon}</span>
              <span>{t(`faculties.${faculty.id}`, { defaultValue: faculty.label })}</span>
            </button>
          ))}
          {activeSpecialty && (
            <button
              className={`${styles.tabButton} ${styles.active}`}
              style={{ marginLeft: 12 }}
              onClick={() => {}}
              tabIndex={-1}
            >
              <span className="material-icons">menu_book</span>
              <span>{activeSpecialty}</span>
              <span
                className={styles.closeSpecialtyTab}
                onClick={handleCloseSpecialty}
                title="Close"
                style={{ marginLeft: 8, cursor: 'pointer' }}
              >
                ×
              </span>
            </button>
          )}
          {activeAcademicLevel && (
            <button
              className={`${styles.tabButton} ${styles.active}`}
              style={{ marginLeft: 12 }}
              onClick={() => {}}
              tabIndex={-1}
            >
              <span className="material-icons">{activeAcademicLevel.icon}</span>
              <span>{activeAcademicLevel.label}</span>
              <span
                className={styles.closeSpecialtyTab}
                onClick={handleCloseAcademicLevel}
                title="Close"
                style={{ marginLeft: 8, cursor: 'pointer' }}
              >
                ×
              </span>
            </button>
          )}
        </div>


        <div className={styles.contentArea}>
          {!activeSpecialty ? (
            <>
              <h2>{t(`faculties.${activeFaculty}`, { defaultValue: faculties.find(f => f.id === activeFaculty)?.label })}</h2>
              <div className={styles.specialtiesList}>
                {specialtiesData[activeFaculty]?.map((specialty, idx) => (
                  <div
                    key={idx}
                    className={styles.specialtyCard}
                    onClick={() => handleSpecialtyClick(specialty)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h3>{specialty}</h3>
                    <p>{t('messages.manageContentFor', { specialty })}</p>
                  </div>
                )) || <p>{t('messages.noSpecialtiesFound')}</p>}
              </div>
            </>
          ) : !activeAcademicLevel ? (
            <>
              <div className={styles.backNavigation}>
                <button 
                  className={styles.backBtn}
                  onClick={handleCloseSpecialty}
                  title={t('messages.backToFacultySelection')}
                >
                  <span className="material-icons">arrow_back</span>
                  {t('messages.backToFaculty', { faculty: t(`faculties.${activeFaculty}`, { defaultValue: faculties.find(f => f.id === activeFaculty)?.label }) })}
                </button>
              </div>
              <h2>{activeSpecialty}</h2>
              <p style={{ marginBottom: 24 }}>
                {t('messages.selectAcademicLevel', { specialty: activeSpecialty })}
              </p>
              <div className={styles.academicLevelsList}>
                {academicLevels.map((level, idx) => (
                  <div
                    key={level.id}
                    className={styles.academicLevelCard}
                    onClick={() => handleAcademicLevelClick(level)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h3>{level.label}</h3>
                    <p>{t('messages.manageArticlesFor', { level: level.label })}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.specialtyManagement}>
              <div className={styles.backNavigation}>
                <button 
                  className={styles.backBtn}
                  onClick={handleCloseAcademicLevel}
                  title={t('messages.backToSpecialtySelection')}
                >
                  <span className="material-icons">arrow_back</span>
                  {t('messages.backToSpecialty', { specialty: activeSpecialty })}
                </button>
              </div>
              <h2>{t('messages.articlesFor', { specialty: activeSpecialty, level: activeAcademicLevel.label })}</h2>
              <p style={{ marginBottom: 24 }}>
                {t('messages.manageFor', { specialty: activeSpecialty, level: activeAcademicLevel.label })}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <input
                  type="text"
                  placeholder={t('filters.searchPlaceholder')}
                  className={styles.searchInput}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: '1rem' }}
                />
                <button className={styles.searchButton} style={{ padding: '10px 18px', borderRadius: 8, background: '#eb8109', color: '#fff', border: 'none', fontWeight: 500, cursor: 'pointer' }}>
                  {t('actions.search')}
                </button>
                <button className={styles.addButton} style={{ padding: '10px 18px', borderRadius: 8, background: '#222', color: '#fff', border: 'none', fontWeight: 500, cursor: 'pointer', marginLeft: 8 }} onClick={handleAddArticle}>
                  {t('actions.addNewArticle')}
                </button>
              </div>
              <h3 style={{ marginTop: 24, marginBottom: 12 }}>{t('messages.articlesFor', { specialty: activeSpecialty, level: activeAcademicLevel.label })}</h3>
              {loading ? (
                <div>{t('messages.loadingArticles')}</div>
              ) : error ? (
                <div className={styles.errorBox} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#eb8109', background: '#fff3e0', borderRadius: 8, padding: '16px 18px', fontWeight: 500 }}>
                  <span className="material-icons" style={{ fontSize: 22 }}>error_outline</span>
                  {error}
                </div>
              ) : articles.length === 0 ? (
                <div style={{ color: '#888', padding: '16px 0' }}>{t('messages.noArticlesFound', { specialty: activeSpecialty, level: activeAcademicLevel.label })}</div>
              ) : (
                <div className={styles.contentCards}>
                  {articles.map(article => (
                    <div key={article.id} className={`${styles.contentCard} ${styles.cardAnimate}`}>
                      <div className={styles.contentCardHeader}>
                        <span className={`${styles.contentTypeBadge} ${article.type || 'vocabulary'}`}>
                          {(() => {
                            // Determine content type based on content analysis
                            const content = article.content || '';
                            const lowerContent = content.toLowerCase();
                            
                            if (lowerContent.includes('vocabulary') || lowerContent.includes('terms') || lowerContent.includes('definitions')) {
                              return t('metadata.vocabulary');
                            } else if (lowerContent.includes('reading') || lowerContent.includes('comprehension') || lowerContent.includes('passage')) {
                              return t('metadata.reading');
                            } else if (lowerContent.includes('writing') || lowerContent.includes('essay') || lowerContent.includes('composition')) {
                              return t('metadata.writing');
                            } else if (lowerContent.includes('grammar') || lowerContent.includes('syntax') || lowerContent.includes('structure')) {
                              return t('metadata.grammar');
                            } else {
                              return t('metadata.article');
                            }
                          })()}
                        </span>
                        <span className={styles.contentDate}>
                          {article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <h4 className={styles.contentTitle}>{article.title}</h4>
                      <p className={styles.contentSpecialty}>{t('metadata.specialty', { specialty: activeSpecialty, level: activeAcademicLevel.label })}</p>
                      <div className={styles.contentPreview}>
                        <div className={styles.previewText}>
                          {article.content ? 
                            (() => {
                              const cleanContent = article.content.replace(/<[^>]*>/g, '').trim();
                              if (cleanContent.length === 0) {
                                return t('metadata.defaultPreview');
                              }
                              const preview = cleanContent.substring(0, 140);
                              return preview.length < cleanContent.length ? `${preview}...` : preview;
                            })() : 
                            t('metadata.defaultPreview')
                          }
                        </div>
                      </div>
                      <div className={styles.cardBottomSection}>
                        <div className={styles.contentActions}>
                          <button 
                            className={`${styles.actionBtn} ${styles.edit}`} 
                            title={t('actions.edit')} 
                            onClick={() => handleEditArticle(article)}
                          >
                            <span className="material-icons">edit</span>
                            {t('actions.edit')}
                          </button>
                          <button 
                            className={`${styles.actionBtn} ${styles.delete}`} 
                            title={t('actions.delete')} 
                            onClick={() => handleDeleteArticle(article.id)}
                          >
                            <span className="material-icons">delete</span>
                            {t('actions.delete')}
                          </button>
                        </div>
                        <div className={styles.articleMetadata}>
                          <div className={styles.metadataItem}>
                            <span className="material-icons">school</span>
                            <span>{activeSpecialty}</span>
                          </div>
                          <div className={styles.metadataItem}>
                            <span className="material-icons">schedule</span>
                            <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                          </div>
                          <div className={styles.metadataItem}>
                            <span className="material-icons">grade</span>
                            <span>{article.difficulty || t('metadata.difficulty')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Add Article Modal */}
              {showAddModal && (
                <div className={styles.modal}>
                  <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                      <h2>{editingArticleId ? t('form.editArticle') : t('form.addNewArticle')}</h2>
                      <button 
                        className={styles.closeButton} 
                        onClick={() => setShowAddModal(false)}
                        title={t('actions.close')}
                      >
                        ×
                      </button>
                    </div>
                    
                    <form onSubmit={handleAddFormSubmit} className={styles.form}>
                      <div className={styles.formGroup}>
                        <label htmlFor="article-title">{t('form.title')}</label>
                        <input
                          type="text"
                          id="article-title"
                          name="title"
                          placeholder={t('form.titlePlaceholder')}
                          value={addForm.title}
                          onChange={handleAddFormChange}
                          required
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="article-language">{t('form.language')}</label>
                        <select
                          id="article-language"
                          name="language"
                          value={addForm.language}
                          onChange={handleAddFormChange}
                          required
                        >
                          <option value="English">English</option>
                          <option value="French">French</option>
                          <option value="Arabic">Arabic</option>
                        </select>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="article-faculty">{t('form.faculty')}</label>
                        <select
                          id="article-faculty"
                          name="faculty"
                          value={addForm.faculty}
                          onChange={handleAddFormChange}
                          required
                        >
                          <option value="">{t('form.selectFaculty')}</option>
                          <option value="Science & Technology">Science & Technology</option>
                          <option value="Engineering">Engineering</option>
                          <option value="Medicine & Health">Medicine & Health</option>
                          <option value="Business & Management">Business & Management</option>
                          <option value="Law & Politics">Law & Politics</option>
                          <option value="Education & Psychology">Education & Psychology</option>
                          <option value="Islamic Sciences">Islamic Sciences</option>
                          <option value="Arts & Humanities">Arts & Humanities</option>
                          <option value="Media & Communication">Media & Communication</option>
                          <option value="Agriculture & Veterinary">Agriculture & Veterinary</option>
                          <option value="Other Specialized">Other Specialized</option>
                        </select>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="article-specialty">{t('form.specialty')}</label>
                        <select
                          id="article-specialty"
                          name="specialty"
                          value={addForm.specialty}
                          onChange={handleAddFormChange}
                          required
                        >
                          <option value="">{t('form.selectSpecialty')}</option>
                          <option value="Physics">Physics</option>
                          <option value="Chemistry">Chemistry</option>
                          <option value="Biology">Biology</option>
                          <option value="Computer Science">Computer Science</option>
                          <option value="Mathematics">Mathematics</option>
                          <option value="Medicine">Medicine</option>
                          <option value="Nursing">Nursing</option>
                          <option value="Pharmacy">Pharmacy</option>
                          <option value="Business Administration">Business Administration</option>
                          <option value="Accounting">Accounting</option>
                          <option value="Finance">Finance</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Law">Law</option>
                          <option value="Political Science">Political Science</option>
                          <option value="Education">Education</option>
                          <option value="Psychology">Psychology</option>
                          <option value="Islamic Law">Islamic Law</option>
                          <option value="Theology">Theology</option>
                          <option value="Literature">Literature</option>
                          <option value="History">History</option>
                          <option value="Philosophy">Philosophy</option>
                          <option value="Fine Arts">Fine Arts</option>
                          <option value="Journalism">Journalism</option>
                          <option value="Digital Media">Digital Media</option>
                          <option value="Film Studies">Film Studies</option>
                          <option value="Agriculture">Agriculture</option>
                          <option value="Veterinary Medicine">Veterinary Medicine</option>
                          <option value="Architecture">Architecture</option>
                          <option value="Urban Planning">Urban Planning</option>
                          <option value="Environmental Science">Environmental Science</option>
                        </select>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="article-academic-year">{t('form.academicYear')}</label>
                        <select
                          id="article-academic-year"
                          name="academic_year"
                          value={addForm.academic_year}
                          onChange={handleAddFormChange}
                          required
                        >
                          <option value="">{t('form.selectAcademicYear')}</option>
                          <option value="Bachelor Year 1">Bachelor Year 1</option>
                          <option value="Bachelor Year 2">Bachelor Year 2</option>
                          <option value="Bachelor Year 3">Bachelor Year 3</option>
                          <option value="Master Year 1">Master Year 1</option>
                          <option value="Master Year 2">Master Year 2</option>
                          <option value="PhD">PhD</option>
                        </select>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor="article-content">{t('form.content')}</label>
                        <textarea
                          id="article-content"
                          name="content"
                          placeholder={t('form.contentPlaceholder')}
                          value={addForm.content}
                          onChange={handleAddFormChange}
                          rows={8}
                          required
                        />
                      </div>
                      
                      {addError && <div className={styles.error}>{addError}</div>}
                      
                      <div className={styles.formActions}>
                        <button type="button" onClick={() => setShowAddModal(false)}>
                          {t('actions.cancel')}
                        </button>
                        <button type="submit">
                          {editingArticleId ? t('actions.update') : t('actions.save')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ESPCourses; 